import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import NewsCard from "../components/NewsCard";
import Reveal from "../components/Reveal";
import { useLang, SECTIONS, DISTRICTS } from "../lib/i18n";

export default function CategoryPage() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const section = SECTIONS.find((s) => s.id === id);
  const [articles, setArticles] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setArticles(null);
    api
      .get(`/articles?category=${id}&limit=40`)
      .then((r) => {
        setArticles(r.data.articles);
        setTotal(r.data.total);
      })
      .catch(() => setArticles([]));
  }, [id]);

  if (!section) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center">{t.noResults}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="category-page">
      {id === "rajasthan" && (
        <div className="relative overflow-hidden border-2 border-ink mb-8" data-testid="rajasthan-hero">
          <img
            src="https://images.unsplash.com/photo-1695956353120-54ce5e91632b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx1ZGFpcHVyJTIwbGFrZSUyMHBpY2hvbGElMjBjaXR5JTIwcGFsYWNlfGVufDB8fHx8MTc5MjkzODcyMXww&ixlib=rb-4.1.0&q=85"
            alt="राजस्थान"
            className="w-full aspect-[16/6] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          <p className="absolute bottom-3 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light/90">
            {lang === "hi" ? "उदयपुर — पिछोला झील" : "Udaipur — Lake Pichola"}
          </p>
        </div>
      )}
      <header className="mb-10">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-brk">
          {lang === "hi" ? "अनुभाग" : "Section"}
        </span>
        <h1 data-testid="category-title" className="font-hindi text-4xl md:text-6xl text-ink mt-1">
          {lang === "hi" ? section.hi : section.en}
        </h1>
        <span className="block gold-rule w-32 mt-4" />
        <p className="font-mono text-xs text-muted-foreground mt-3">
          {total} {t.stories}
        </p>
      </header>

      {id === "rajasthan" && (
        <div className="flex flex-wrap gap-2 mb-10" data-testid="district-chips">
          {DISTRICTS.map((d) => (
            <Link
              key={d.id}
              to={`/district/${d.id}`}
              data-testid={`chip-${d.id}`}
              className="px-3.5 py-1.5 text-xs font-semibold border border-ink/25 text-ink hover:border-deepgreen hover:text-deepgreen transition-colors"
            >
              {lang === "hi" ? d.hi : d.en}
            </Link>
          ))}
        </div>
      )}

      {!articles ? (
        <div className="text-center font-mono text-sm text-muted-foreground py-10">…</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="category-empty">
          {t.noResults}
        </div>
      ) : (
        <>
          <Reveal>
            <div className="grid md:grid-cols-2 gap-8 mb-10 pb-10 border-b-2 border-ink">
              <NewsCard article={articles[0]} variant="standard" />
              <div>
                {articles.slice(1, 5).map((a) => (
                  <NewsCard key={a.id} article={a} variant="list" />
                ))}
              </div>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {articles.slice(5).map((a, i) => (
              <Reveal key={a.id} delay={Math.min(i * 0.06, 0.4)}>
                <NewsCard article={a} variant="standard" />
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
